import React, { memo, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    getFilteredRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    flexRender
} from '@tanstack/react-table';
import { useI18n, getText } from '../context/I18nContext';

/**
 * 安全渲染單元格內容，處理多語言物件
 * @param {any} content - 單元格內容
 * @param {Function} getTextFn - getText 函數
 * @returns {React.ReactNode}
 */
function safeRenderCell(content, getTextFn) {
    // 如果是 null 或 undefined，返回空字符串
    if (content == null) return '';
    
    // 如果是 React 元素，直接返回
    if (React.isValidElement(content)) return content;
    
    // 如果是多語言物件（包含 en, zh-TW, zh-CN 等鍵），使用 getText 處理
    if (typeof content === 'object' && !Array.isArray(content)) {
        // 檢查是否為多語言物件
        const keys = Object.keys(content);
        const langKeys = ['en', 'zh-TW', 'zh-CN', 'zh_TW', 'zh_CN'];
        if (keys.some(k => langKeys.includes(k))) {
            return getTextFn ? getTextFn(content) : (content.en || content['zh-TW'] || JSON.stringify(content));
        }
        // 其他物件轉為 JSON 字符串或返回空
        return '';
    }
    
    // 如果是陣列，返回空或 join
    if (Array.isArray(content)) {
        return content.map(item => safeRenderCell(item, getTextFn)).join(', ');
    }
    
    // 基本類型直接返回
    return content;
}

/**
 * DataTable Component
 * 
 * 支持兩種分頁模式：
 * 1. 客戶端分頁（默認）：數據全部載入，由 DataTable 內部處理分頁
 * 2. 服務端分頁：傳入 pagination 和 onPageChange props
 * 
 * Props:
 * - data: 表格數據
 * - columns: 列定義
 * - title: 標題（可選）
 * - pagination: 服務端分頁資訊 { page, limit, total, totalPages }（可選）
 * - onPageChange: 頁碼變更回調（可選）
 * - onPageSizeChange: 頁大小變更回調（可選）
 */

const DataTable = memo(({ 
    data, 
    columns, 
    title,
    pagination: serverPagination,  // 服務端分頁資訊
    onPageChange,                  // 服務端分頁回調
    onPageSizeChange,              // 頁大小變更回調
    isLoading = false              // 載入中狀態（用於顯示骨架行）
}) => {
    const { t, language } = useI18n(['datatable']);
    
    // 創建 getText 函數用於處理多語言物件
    const getTextFn = useCallback((textObj) => {
        return getText(textObj, language);
    }, [language]);
    
    // Table State
    const [sorting, setSorting] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [columnFilters, setColumnFilters] = useState([]);
    const [columnSizing, setColumnSizing] = useState({});
    const [columnSizingInfo, setColumnSizingInfo] = useState({});
    const [clientPagination, setClientPagination] = useState({
        pageIndex: 0,
        pageSize: 20,
    });

    const [openFilterId, setOpenFilterId] = useState(null);
    const [filterSearchTerms, setFilterSearchTerms] = useState({});
    const filterContainerRefs = useRef({});

    // 判斷是否為服務端分頁模式
    const isServerPagination = !!serverPagination && !!onPageChange;

    // Memoize page size options
    const pageSizeOptions = useMemo(() => [10, 20, 50, 100], []);

    const normalizeFilterValue = useCallback((value) => {
        if (Array.isArray(value)) {
            return value
                .map(item => normalizeFilterValue(item))
                .filter(Boolean)
                .join(', ');
        }

        const rendered = safeRenderCell(value, getTextFn);

        if (React.isValidElement(rendered)) return '';
        if (rendered == null) return '';
        if (typeof rendered === 'string') return rendered.trim();
        if (typeof rendered === 'number' || typeof rendered === 'boolean') return String(rendered);

        return '';
    }, [getTextFn]);

    const filterFns = useMemo(() => ({
        multiSelect: (row, columnId, selectedValues) => {
            if (!selectedValues || selectedValues.length === 0) return true;

            const value = row.getValue(columnId);
            const normalizedValue = normalizeFilterValue(value);

            if (Array.isArray(value)) {
                return value.some(item => selectedValues.includes(normalizeFilterValue(item)));
            }

            return selectedValues.includes(normalizedValue);
        }
    }), [normalizeFilterValue]);

    const table = useReactTable({
        data: data || [],
        columns,
        defaultColumn: {
            size: 160,
            minSize: 80,
            maxSize: 800,
        },
        state: {
            sorting,
            globalFilter,
            columnFilters,
            columnSizing,
            columnSizingInfo,
            pagination: clientPagination,
        },
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        onColumnFiltersChange: setColumnFilters,
        onColumnSizingChange: setColumnSizing,
        onColumnSizingInfoChange: setColumnSizingInfo,
        onPaginationChange: setClientPagination,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        enableMultiSort: true,
        isMultiSortEvent: (e) => e.shiftKey,
        // 服務端分頁時不使用客戶端分頁
        ...(isServerPagination ? {} : {
            getPaginationRowModel: getPaginationRowModel(),
        }),
        manualPagination: isServerPagination,
        filterFns,
        pageCount: isServerPagination ? serverPagination.totalPages : undefined,
    });

    const filterableColumns = useMemo(() => {
        return table.getAllColumns().filter(col => col.columnDef?.meta?.filterable);
    }, [table]);

    const getFilterOptions = useCallback((column) => {
        const faceted = column.getFacetedUniqueValues();
        const searchTerm = (filterSearchTerms[column.id] || '').toLowerCase();

        return Array.from(faceted.entries())
            .map(([rawValue, count]) => {
                const label = normalizeFilterValue(rawValue) || t('table.empty', { ns: 'datatable', defaultValue: '—' });
                return {
                    rawValue,
                    value: normalizeFilterValue(rawValue),
                    label,
                    count
                };
            })
            .filter(option => option.value !== undefined)
            .filter(option => option.label.toLowerCase().includes(searchTerm))
            .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    }, [filterSearchTerms, normalizeFilterValue, t]);

    const handleToggleFilterValue = useCallback((columnId, value) => {
        const column = table.getColumn(columnId);
        if (!column) return;

        const current = Array.isArray(column.getFilterValue()) ? column.getFilterValue() : [];
        const next = current.includes(value)
            ? current.filter(v => v !== value)
            : [...current, value];

        column.setFilterValue(next.length > 0 ? next : undefined);
    }, [table]);

    const handleSelectAll = useCallback((columnId, options) => {
        const column = table.getColumn(columnId);
        if (!column) return;
        const values = options.map(opt => opt.value).filter(Boolean);
        column.setFilterValue(values.length > 0 ? values : undefined);
    }, [table]);

    const handleSelectNone = useCallback((columnId) => {
        const column = table.getColumn(columnId);
        column?.setFilterValue(undefined);
    }, [table]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!openFilterId) return;

            const refNode = filterContainerRefs.current[openFilterId];
            if (refNode && !refNode.contains(event.target)) {
                setOpenFilterId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openFilterId]);

    // Memoize handler callbacks for client-side pagination
    const handlePageSizeChange = useCallback((e) => {
        table.setPageSize(Number(e.target.value));
    }, [table]);

    const handleGlobalFilterChange = useCallback((e) => {
        setGlobalFilter(e.target.value);
    }, []);

    const handleAutoSizeColumn = useCallback((columnId) => {
        if (typeof document === 'undefined') return;
        const column = table.getColumn(columnId);
        if (!column) return;

        const nodes = document.querySelectorAll(`[data-col-id="${columnId}"]`);
        let maxWidth = 0;

        nodes.forEach(node => {
            const contentNode = node.querySelector('[data-cell-content]') || node;
            const width = Math.max(node.scrollWidth, contentNode.scrollWidth);
            if (width > maxWidth) maxWidth = width;
        });

        const padded = maxWidth + 24;
        const nextSize = Math.min(Math.max(padded, 80), 800);
        column.setSize(nextSize);
    }, [table]);

    // 客戶端分頁處理
    const handleFirstPage = useCallback(() => table.setPageIndex(0), [table]);
    const handlePreviousPage = useCallback(() => table.previousPage(), [table]);
    const handleNextPage = useCallback(() => table.nextPage(), [table]);
    const handleLastPage = useCallback(() => table.setPageIndex(table.getPageCount() - 1), [table]);

    // 服務端分頁處理
    const handleServerFirstPage = useCallback(() => onPageChange?.(1), [onPageChange]);
    const handleServerPreviousPage = useCallback(() => {
        if (serverPagination?.page > 1) {
            onPageChange?.(serverPagination.page - 1);
        }
    }, [onPageChange, serverPagination?.page]);
    const handleServerNextPage = useCallback(() => {
        if (serverPagination?.page < serverPagination?.totalPages) {
            onPageChange?.(serverPagination.page + 1);
        }
    }, [onPageChange, serverPagination?.page, serverPagination?.totalPages]);
    const handleServerLastPage = useCallback(() => {
        onPageChange?.(serverPagination?.totalPages || 1);
    }, [onPageChange, serverPagination?.totalPages]);

    // Memoize pagination info
    const paginationInfo = useMemo(() => {
        if (isServerPagination) {
            const { page = 1, limit = 10, total = 0, totalPages = 0 } = serverPagination;
            const startRow = total === 0 ? 0 : (page - 1) * limit + 1;
            const endRow = Math.min(page * limit, total);
            return { 
                startRow, 
                endRow, 
                totalRows: total, 
                currentPage: page, 
                totalPages,
                canPrevious: page > 1,
                canNext: page < totalPages
            };
        } else {
            const totalRows = table.getFilteredRowModel().rows.length;
            const pageSize = table.getState().pagination.pageSize;
            const pageIndex = table.getState().pagination.pageIndex;
            const startRow = totalRows === 0 ? 0 : pageIndex * pageSize + 1;
            const endRow = Math.min((pageIndex + 1) * pageSize, totalRows);
            const currentPage = pageIndex + 1;
            const totalPages = table.getPageCount();
            return { 
                startRow, 
                endRow, 
                totalRows, 
                currentPage, 
                totalPages,
                canPrevious: table.getCanPreviousPage(),
                canNext: table.getCanNextPage()
            };
        }
    }, [table, clientPagination, globalFilter, isServerPagination, serverPagination]);

    // 空數據顯示
    const rowsToRender = isServerPagination 
        ? table.getRowModel().rows 
        : table.getRowModel().rows;

    return (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            {title && <h3 className="text-xl font-semibold text-gray-800 mb-4">{title}</h3>}

            {filterableColumns.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                    {filterableColumns.map(column => {
                        const selectedValues = Array.isArray(column.getFilterValue()) ? column.getFilterValue() : [];
                        const options = getFilterOptions(column);
                        const labelFromHeader = typeof column.columnDef.header === 'string'
                            ? column.columnDef.header
                            : column.columnDef.meta?.filterLabel || column.id;

                        return (
                            <div
                                key={column.id}
                                className="relative"
                                ref={(node) => {
                                    if (node) filterContainerRefs.current[column.id] = node;
                                }}
                            >
                                <button
                                    type="button"
                                    onClick={() => setOpenFilterId(prev => prev === column.id ? null : column.id)}
                                    className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50 shadow-sm"
                                >
                                    <span className="text-gray-700">{labelFromHeader}</span>
                                    {selectedValues.length > 0 && (
                                        <span className="text-blue-600 font-medium">{selectedValues.length}</span>
                                    )}
                                    <span className="text-gray-500">▾</span>
                                </button>

                                {openFilterId === column.id && (
                                    <div className="absolute z-20 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                                        <div className="flex items-center gap-2 mb-3">
                                            <input
                                                type="text"
                                                value={filterSearchTerms[column.id] || ''}
                                                onChange={(e) => setFilterSearchTerms(prev => ({ ...prev, [column.id]: e.target.value }))}
                                                placeholder={t('table.searchPlaceholder', { ns: 'datatable' })}
                                                className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-sm"
                                            />
                                            <button
                                                type="button"
                                                className="px-2 py-1 text-xs border border-gray-300 rounded-md hover:bg-gray-50"
                                                onClick={() => handleSelectAll(column.id, options)}
                                            >
                                                {t('table.all', { ns: 'datatable', defaultValue: 'All' })}
                                            </button>
                                            <button
                                                type="button"
                                                className="px-2 py-1 text-xs border border-gray-300 rounded-md hover:bg-gray-50"
                                                onClick={() => handleSelectNone(column.id)}
                                            >
                                                {t('table.none', { ns: 'datatable', defaultValue: 'None' })}
                                            </button>
                                        </div>

                                        <div className="max-h-64 overflow-y-auto pr-1 space-y-2">
                                            {options.length === 0 && (
                                                <div className="text-sm text-gray-500 px-1">
                                                    {t('table.noData', { ns: 'datatable' })}
                                                </div>
                                            )}

                                            {options.map(option => (
                                                <label
                                                    key={`${column.id}-${option.label}`}
                                                    className="flex items-center gap-2 px-1"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                                        checked={selectedValues.includes(option.value)}
                                                        onChange={() => handleToggleFilterValue(column.id, option.value)}
                                                    />
                                                    <span className="flex-1 text-sm text-gray-700 truncate" title={option.label}>
                                                        {option.label}
                                                    </span>
                                                    <span className="text-xs text-gray-500">{option.count}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full border-collapse table-fixed">
                    <thead className="bg-gray-50">
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <th
                                        key={header.id}
                                        className="relative px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b-2 border-gray-200 cursor-pointer hover:bg-gray-100"
                                        onClick={header.column.getToggleSortingHandler()}
                                        style={{ width: header.getSize() }}
                                        data-col-id={header.column.id}
                                    >
                                        <div className="flex items-center gap-2" data-cell-content>
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                            {{
                                                asc: (
                                                    <span>
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                                        </svg>
                                                    </span>
                                                ),
                                                desc: (
                                                    <span>
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                    </span>
                                                ),
                                                none: (
                                                    <span>
                                                    </span>
                                                ),
                                            }[header.column.getIsSorted()] ?? null}
                                        </div>
                                        {header.column.getCanResize() && (
                                            <div
                                                onClick={(e) => e.stopPropagation()}
                                                onMouseDown={header.getResizeHandler()}
                                                onTouchStart={header.getResizeHandler()}
                                                onDoubleClick={(e) => {
                                                    e.stopPropagation();
                                                    handleAutoSizeColumn(header.column.id);
                                                }}
                                                className={`absolute right-0 top-0 h-full w-1.5 cursor-col-resize select-none bg-transparent hover:bg-blue-200 ${header.column.getIsResizing() ? 'bg-blue-300' : ''}`}
                                            />
                                        )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {isLoading ? (
                            // 骨架行：保持表格結構，內容顯示 loading 動畫
                            Array.from({ length: serverPagination?.limit || 10 }).map((_, rowIndex) => (
                                <tr key={`skeleton-${rowIndex}`} className="animate-pulse">
                                    {columns.map((col, colIndex) => (
                                        <td
                                            key={`skeleton-${rowIndex}-${colIndex}`}
                                            className="px-4 py-3 text-sm border-b border-gray-200"
                                            style={{ width: col.size || 160 }}
                                        >
                                            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : rowsToRender.length > 0 ? (
                            rowsToRender.map(row => (
                                <tr key={row.id} className="hover:bg-gray-50">
                                    {row.getVisibleCells().map(cell => {
                                        // 獲取渲染內容
                                        const rendered = flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext()
                                        );
                                        // 安全處理多語言物件
                                        const safeContent = safeRenderCell(rendered, getTextFn);
                                        
                                        return (
                                            <td
                                                key={cell.id}
                                                className="px-4 py-3 text-sm text-gray-700 border-b border-gray-200 whitespace-nowrap"
                                                style={{ width: cell.column.getSize() }}
                                                data-col-id={cell.column.id}
                                            >
                                                <span data-cell-content>{safeContent}</span>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td 
                                    colSpan={columns.length} 
                                    className="px-4 py-8 text-sm text-gray-500 text-center"
                                >
                                    {t('table.noData', { ns: 'datatable' })}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-4">
                {/* Search and Controls */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-700">{t('table.showing', { ns: 'datatable' })}</label>
                        <select
                            value={isServerPagination ? (serverPagination?.limit || 20) : table.getState().pagination.pageSize}
                            onChange={isServerPagination
                                ? (e) => onPageSizeChange?.(Number(e.target.value))
                                : handlePageSizeChange
                            }
                            className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                        >
                            {pageSizeOptions.map(pageSize => (
                                <option key={pageSize} value={pageSize}>
                                    {pageSize}
                                </option>
                            ))}
                        </select>
                        <label className="text-sm text-gray-700">{t('table.rows', { ns: 'datatable' })}</label>
                    </div>

                    {/* Search - 只在客戶端分頁時顯示 */}
                    {!isServerPagination && (
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-700">{t('table.search', { ns: 'datatable' })}</label>
                            <input
                                type="text"
                                value={globalFilter ?? ''}
                                onChange={handleGlobalFilterChange}
                                className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                                placeholder={t('table.searchPlaceholder', { ns: 'datatable' })}
                            />
                        </div>
                    )}
                </div>
                
                <div className="text-sm text-gray-700">
                    {paginationInfo.totalRows > 0 ? (
                        <>{t('table.pageInfo', { ns: 'datatable', start: paginationInfo.startRow, end: paginationInfo.endRow, total: paginationInfo.totalRows })}</>
                        
                    ) : (
                        <>{t('table.pageInfo', { ns: 'datatable', start: 0, end: 0, total: 0 })}</>
                    )}
                </div>
                
                <div className="flex items-center gap-2">
                    <button
                        onClick={isServerPagination ? handleServerFirstPage : handleFirstPage}
                        disabled={!paginationInfo.canPrevious}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                        {'<<'}
                    </button>
                    <button
                        onClick={isServerPagination ? handleServerPreviousPage : handlePreviousPage}
                        disabled={!paginationInfo.canPrevious}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                        {'<'}
                    </button>
                    
                    <span className="px-3 py-1 text-sm text-gray-700">
                        {t('table.paginationInfo', { ns: 'datatable', start: paginationInfo.currentPage, total: paginationInfo.totalPages })}
                    </span>
                    
                    <button
                        onClick={isServerPagination ? handleServerNextPage : handleNextPage}
                        disabled={!paginationInfo.canNext}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                        {'>'}
                    </button>
                    <button
                        onClick={isServerPagination ? handleServerLastPage : handleLastPage}
                        disabled={!paginationInfo.canNext}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                        {'>>'}
                    </button>
                </div>
            </div>
        </div>
    );
});

DataTable.displayName = 'DataTable';

export default DataTable;
